import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Pencil, Mail, CheckCircle2, FileInput } from "lucide-react";
import { InvoiceEditor } from "./InvoiceEditor";
import { API, selectCls, eur, dstr, btnGhost, btnRed } from "./shared";

const STATUS_STYLE = {
  entwurf: "border-white/30 text-white/60",
  gesendet: "border-sky-400/50 text-sky-300",
  bezahlt: "border-emerald-400/50 text-emerald-300",
  ueberfaellig: "border-[#ff4d5e]/60 text-[#ff8090]",
  storniert: "border-white/15 text-white/35 line-through",
};
const STATUS_LABEL = { entwurf: "Entwurf", gesendet: "Gesendet", bezahlt: "Bezahlt", ueberfaellig: "Überfällig", storniert: "Storniert" };

export const Invoices = ({ onPrint, canConvert = true }) => {
  const [invoices, setInvoices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [editing, setEditing] = useState(undefined); // undefined = Liste, null = neu
  const [convertId, setConvertId] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/invoices`, { withCredentials: true }).then(({ data }) => setInvoices(data)).catch(() => {});
    axios.get(`${API}/offers`, { withCredentials: true }).then(({ data }) => setOffers(data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const convert = async () => {
    if (!convertId) return;
    try {
      const { data } = await axios.post(`${API}/offers/${convertId}/to-invoice`, {}, { withCredentials: true });
      toast.success(`Rechnung ${data.number} aus Angebot erstellt.`);
      setConvertId("");
      load();
      setEditing(data);
    } catch {
      toast.error("Umwandlung fehlgeschlagen.");
    }
  };

  const del = async (id) => {
    await axios.delete(`${API}/invoices/${id}`, { withCredentials: true }).catch(() => toast.error("Löschen fehlgeschlagen."));
    setInvoices((xs) => xs.filter((x) => x.id !== id));
  };

  const markPaid = async (inv) => {
    try {
      const { data } = await axios.patch(`${API}/invoices/${inv.id}`, { status: "bezahlt" }, { withCredentials: true });
      toast.success(`${inv.number} als bezahlt markiert – Einnahme wurde gebucht.`);
      setInvoices((xs) => xs.map((x) => (x.id === inv.id ? data : x)));
    } catch {
      toast.error("Fehler beim Aktualisieren.");
    }
  };

  const sendMail = async (inv) => {
    try {
      await axios.post(`${API}/invoices/${inv.id}/send`, {}, { withCredentials: true });
      toast.success(`Rechnung ${inv.number} per E-Mail gesendet.`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "E-Mail-Versand fehlgeschlagen.");
    }
  };

  if (editing !== undefined) {
    return (
      <InvoiceEditor
        invoice={editing}
        onClose={() => setEditing(undefined)}
        onSaved={() => load()}
        onPrint={onPrint}
      />
    );
  }

  return (
    <div data-testid="invoices-view">
      <div className="flex flex-wrap gap-3 mb-6">
        <button data-testid="invoice-new" onClick={() => setEditing(null)} className={btnRed}>
          <Plus size={15} /> Neue Rechnung
        </button>
        {canConvert && (
          <div className="flex gap-2 flex-1 min-w-[260px]">
            <select data-testid="invoice-from-offer" value={convertId} onChange={(e) => setConvertId(e.target.value)} className={`${selectCls} flex-1`}>
              <option value="">Aus Angebot umwandeln …</option>
              {offers.map((o) => (
                <option key={o.id} value={o.id}>{o.number} · {o.customer_name}</option>
              ))}
            </select>
            <button data-testid="invoice-convert" onClick={convert} disabled={!convertId} className={`${btnGhost} disabled:opacity-40`}>
              <FileInput size={14} /> Umwandeln
            </button>
          </div>
        )}
      </div>

      {invoices.length === 0 ? (
        <div data-testid="invoices-empty" className="bg-[#1e2226] border border-white/10 rounded-2xl p-14 text-center text-white/50">
          Noch keine Rechnungen – lege oben die erste an oder wandle ein Angebot um.
        </div>
      ) : (
        <div data-testid="invoices-list" className="space-y-3">
          {invoices.map((inv) => {
            const net = inv.items.reduce((s, it) => s + it.qty * it.unit_price, 0);
            const gross = net * (1 + (inv.vat_rate ?? 19) / 100);
            return (
              <div key={inv.id} data-testid={`invoice-${inv.id}`} className="bg-[#1e2226] border border-white/10 rounded-2xl p-5 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-display font-semibold">{inv.number} · {inv.customer_name}</p>
                  <p className="text-white/45 text-xs mt-1">
                    {dstr(inv.invoice_date)} · fällig {dstr(inv.due_date)} · {(inv.vat_rate ?? 19)} % MwSt.
                    {inv.offer_id && " · aus Angebot"}
                  </p>
                </div>
                <span data-testid={`invoice-status-badge-${inv.id}`} className={`text-[10px] font-medium uppercase tracking-widest border rounded-full px-3 py-1 ${STATUS_STYLE[inv.status] || STATUS_STYLE.entwurf}`}>
                  {STATUS_LABEL[inv.status] || inv.status}
                </span>
                <p className="font-display font-bold text-lg text-white">{eur(gross)}</p>
                <div className="flex flex-wrap gap-2">
                  {inv.status !== "bezahlt" && inv.status !== "storniert" && (
                    <button data-testid={`invoice-paid-${inv.id}`} onClick={() => markPaid(inv)} className={btnGhost} title="Als bezahlt markieren (bucht automatisch eine Einnahme)">
                      <CheckCircle2 size={14} /> Bezahlt
                    </button>
                  )}
                  <button data-testid={`invoice-print-${inv.id}`} onClick={() => onPrint(inv)} className={btnGhost}>
                    <Printer size={14} /> Drucken
                  </button>
                  <button data-testid={`invoice-mail-${inv.id}`} onClick={() => sendMail(inv)} className={btnGhost}>
                    <Mail size={14} /> E-Mail
                  </button>
                  <button data-testid={`invoice-edit-${inv.id}`} onClick={() => setEditing(inv)} className={btnGhost}>
                    <Pencil size={14} /> Bearbeiten
                  </button>
                  <button data-testid={`invoice-delete-${inv.id}`} onClick={() => del(inv.id)} className="text-white/30 hover:text-[#ff4d5e] transition-colors p-2" aria-label="Rechnung löschen">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
